// SearchBarForPatientLanding.tsx - UPDATED WITH FIXES
import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  Button,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  SxProps,
  Theme,
  SvgIconProps,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  LocationOn,
  Search,
  HealthAndSafety,
  Psychology,
  MyLocation,
} from '@mui/icons-material'
import api from '@/services/api'
import { useGooglePlaces, PlaceDetails } from '@/hooks/useGooglePlaces'
import { usePatientSearchStore } from '@/store/usePatientSearchStore'
import { SEARCH_RADIUS_OPTIONS } from '@/constants/searchConstants'

// Types for API responses
interface InsuranceCompany {
  _id: string
  payer_name: string
  payerid: string
}

interface Specialty {
  _id: string
  name: string
  description?: string
}

interface LocationData {
  address: string
  latitude: number
  longitude: number
}

interface SearchParams {
  insurance?: string
  specialty?: string
  radius?: string
  location?: string
  lat?: number
  lng?: number
  insuranceId?: string
  specialtyId?: string
}

type AutocompleteOption = Record<string, any> | string

interface SearchBarForPatientLandingProps {
  location: string | null
  insurance: string | null
  specialization: string | null
  radius?: string | null
  locationOptions?: AutocompleteOption[]
  insuranceOptions?: AutocompleteOption[]
  specializationOptions?: AutocompleteOption[]
  locationLoading?: boolean
  insuranceLoading?: boolean
  specializationLoading?: boolean
  onLocationChange: (value: string | null) => void
  onInsuranceChange: (value: string | null) => void
  onSpecializationChange: (value: string | null) => void
  onRadiusChange?: (value: string | null) => void
  onLocationSelect?: (option: AutocompleteOption | null) => void
  onInsuranceSelect?: (option: AutocompleteOption | null) => void
  onSpecializationSelect?: (option: AutocompleteOption | null) => void
  onLocationInputChange?: (value: string) => void
  onInsuranceInputChange?: (value: string) => void
  onSpecializationInputChange?: (value: string) => void
  onSearch?: (searchParams: SearchParams) => void
  needs: string | null
  onNeedsChange: (value: string | null) => void
  isSearchLoading?: boolean
  showSearchButton?: boolean
  compact?: boolean
  disableHoverExpand?: boolean
  sx?: SxProps<Theme>
  accentColor?: string
  accentHoverColor?: string
  thirdFieldLabel?: string
  thirdFieldPlaceholder?: string
  thirdFieldIcon?: React.ElementType<SvgIconProps>
}

const getOptionLabel = (option: AutocompleteOption, fallback = '') => {
  if (!option) return fallback
  if (typeof option === 'string') return option
  return (
    option.label ??
    option.name ??
    option.payer_name ??
    option.description ??
    option.title ??
    fallback
  )
}

const SearchBarForPatientLanding: React.FC<SearchBarForPatientLandingProps> = ({
  location,
  insurance,
  specialization,
  radius,
  needs,
  onNeedsChange,
  insuranceOptions,
  specializationOptions,
  locationLoading = false,
  insuranceLoading = false,
  specializationLoading = false,
  onLocationChange,
  onInsuranceChange,
  onSpecializationChange,
  onRadiusChange,
  onLocationSelect,
  onInsuranceSelect,
  onSpecializationSelect,
  onLocationInputChange,
  onInsuranceInputChange,
  onSpecializationInputChange,
  onSearch,
  isSearchLoading = false,
  showSearchButton = true,
  compact = false,
  disableHoverExpand = false,
  sx,
  accentColor = '#2E7DAF',
  accentHoverColor = '#2563a0',
  thirdFieldLabel = 'Specialization',
  thirdFieldPlaceholder = 'Enter specialization',
  thirdFieldIcon,
}) => {
  const [hoveredField, setHoveredField] = React.useState<
    'location' | 'radius' | 'insurance' | 'specialization' | null
  >(null)

  const ThirdFieldIcon = thirdFieldIcon ?? Psychology

  // State for dynamic data
  const [insuranceCompanies, setInsuranceCompanies] = useState<
    InsuranceCompany[]
  >([])
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingSpecialties, setLoadingSpecialties] = useState(false)
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('')
  const [specialtySearchTerm, setSpecialtySearchTerm] = useState('')

  // Location states
  const [locationInput, setLocationInput] = useState('')
  const [locationOptions, setLocationOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    null
  )
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false)

  // ✅ Use patient search store
  const {
    setRadius: setStoreRadius,
    setSpecialty,
    setInsurance,
    setLocation: setStoreLocation,
    location: storeLocation,
    radius: storeRadius,
    insuranceId,
    specialtyId,
    insuranceName,
    specialtyName,
    clearSearch,
  } = usePatientSearchStore()

  // Use your Google Places hook
  const {
    isLoaded,
    isLoading: isGoogleLoading,
    searchPlaces,
    getPlaceDetails,
    getCurrentLocation,
  } = useGooglePlaces()

  // Add ref to track the last selected specialty
  const lastSelectedSpecialtyRef = useRef<{
    id: string | null
    name: string | null
  }>({ id: null, name: null })

  // ✅ Sync component state with store state for location
  useEffect(() => {
    if (storeLocation?.address && storeLocation.address !== locationInput) {
      setLocationInput(storeLocation.address)
      setSelectedLocation({
        address: storeLocation.address,
        latitude: storeLocation.lat || 0,
        longitude: storeLocation.lng || 0,
      })
    }
  }, [storeLocation])

  // ✅ Sync radius
  useEffect(() => {
    if (storeRadius && radius !== storeRadius) {
      onRadiusChange?.(storeRadius + ' miles')
    }
  }, [storeRadius])

  // ✅ Sync insurance
  useEffect(() => {
    if (insuranceName && insurance !== insuranceName) {
      onInsuranceChange(insuranceName)
    }
  }, [insuranceName])

  // Fetch insurance companies
  const fetchInsuranceCompanies = async (search: string = '') => {
    setLoadingCompanies(true)
    try {
      const response = await api.get(`/payers/all?search=${search}`, {
        timeout: 15000,
      })
      const companies = response.data?.data || response.data || []
      setInsuranceCompanies(companies)
    } catch (error: any) {
      console.error('Error fetching insurance companies:', error)
      setInsuranceCompanies([])
    } finally {
      setLoadingCompanies(false)
    }
  }

  // Fetch specialties with search - UPDATED WITH DEBUGGING
  const fetchSpecialties = async (search: string = '') => {
    setLoadingSpecialties(true)
    try {
      const response = await api.get(
        `specilities/specility${search ? `?search=${search}` : ''}`,
        {
          timeout: 15000,
        }
      )
      const specialtiesData = response.data?.data || response.data || []
      setSpecialties(specialtiesData)
    } catch (error: any) {
      console.error('Error fetching specialties:', error)
      setSpecialties([])
    } finally {
      setLoadingSpecialties(false)
    }
  }

  // Fetch location predictions from Google Places
  const fetchLocationPredictions = async (input: string) => {
    if (!input.trim()) {
      setLocationOptions([])
      return
    }

    setLoadingLocations(true)
    try {
      const predictions = await searchPlaces(input)
      setLocationOptions(predictions)
    } catch (error) {
      console.error('Error fetching location predictions:', error)
      setLocationOptions([])
    } finally {
      setLoadingLocations(false)
    }
  }

  // Handle specialty change - UPDATED FIX
  const handleSpecialtyChange = (
    value: string | Specialty | null,
    reason?: string
  ) => {
    if (!value) {
      onNeedsChange(null)
      setSpecialty(null, null)
      lastSelectedSpecialtyRef.current = { id: null, name: null }
      return
    }

    if (typeof value === 'string') {
      // Custom input
      onNeedsChange(value)
      setSpecialty(null, value)
      lastSelectedSpecialtyRef.current = { id: null, name: value }
    } else {
      // Selected from dropdown
      onNeedsChange(value.name)
      setSpecialty(value._id, value.name)
      lastSelectedSpecialtyRef.current = { id: value._id, name: value.name }
    }
  }

  const StyledInput = ({
    icon,
    placeholder,
    compact,
    InputProps,
    ...params
  }: any) => (
    <TextField
      {...params}
      placeholder={placeholder}
      variant="standard"
      InputProps={{
        ...params.InputProps,
        ...InputProps,
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              mr: { xs: 0.5, sm: 1 },
              minWidth: 'auto',
            }}
          >
            {icon}
          </InputAdornment>
        ),
        disableUnderline: true,
        sx: {
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          height: compact
            ? { xs: '28px', sm: '32px' }
            : { xs: '36px', sm: '40px' },
          '& .MuiInputBase-input': {
            padding: compact ? '4px 0' : '8px 0',
            fontSize: 'inherit',
            '&::placeholder': {
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
              opacity: 0.7,
            },
          },
        },
      }}
      sx={{
        width: '100%',
        minWidth: '0 !important',
        '& .MuiInputBase-root': {
          padding: 0,
          height: '100%',
        },
        ...params.sx,
      }}
    />
  )

  // Handle current location button click
  const handleCurrentLocation = async () => {
    setGettingCurrentLocation(true)
    try {
      const currentLocation = await getCurrentLocation()
      setSelectedLocation(currentLocation)
      onLocationChange(currentLocation.address)
      setLocationInput(currentLocation.address)
      // ✅ Update patient store
      setStoreLocation({
        address: currentLocation.address,
        lat: currentLocation.latitude,
        lng: currentLocation.longitude,
      })
    } catch (error) {
      console.error('Error getting current location:', error)
    } finally {
      setGettingCurrentLocation(false)
    }
  }

  // Handle location selection with coordinates
  const handleLocationSelect = async (placeId: string, description: string) => {
    try {
      const placeDetails = await getPlaceDetails(placeId)
      setSelectedLocation(placeDetails)
      // ✅ Update patient store
      setStoreLocation({
        address: description,
        lat: placeDetails.latitude,
        lng: placeDetails.longitude,
      })

      setLocationInput(description)
    } catch (error) {
      console.error('Error getting place details:', error)
      const tempLocation = {
        address: description,
        latitude: 0,
        longitude: 0,
      }
      setSelectedLocation(tempLocation)
      // ✅ Update patient store
      setStoreLocation({
        address: description,
        lat: 0,
        lng: 0,
      })
      setLocationInput(description)
    }
  }

  // Handle search with coordinates - UPDATED WITH DIRECT STORE ACCESS
  const handleSearch = () => {
    // Get the current store state directly
    const currentStore = usePatientSearchStore.getState()

    // Use store state directly, fallback to ref if needed
    const effectiveSpecialtyId =
      currentStore.specialtyId || lastSelectedSpecialtyRef.current.id
    const effectiveSpecialtyName =
      currentStore.specialtyName ||
      lastSelectedSpecialtyRef.current.name ||
      needs

    onSearch?.({
      insurance: currentStore.insuranceName || insurance || null,
      specialty: effectiveSpecialtyName || null,
      insuranceId: currentStore.insuranceId || null,
      specialtyId: effectiveSpecialtyId || null,
      radius: currentStore.radius || radius?.replace(' miles', '') || null,
      location:
        currentStore.location?.address ||
        selectedLocation?.address ||
        locationInput ||
        null,
      lat: currentStore.location?.lat || selectedLocation?.latitude || null,
      lng: currentStore.location?.lng || selectedLocation?.longitude || null,
    })
  }

  // Debounced effect for location predictions
  useEffect(() => {
    if (!isLoaded) return

    const delayDebounceFn = setTimeout(() => {
      fetchLocationPredictions(locationInput)
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [locationInput, isLoaded])

  // Effects for initial data loading
  useEffect(() => {
    fetchSpecialties()
  }, [])

  // Effect for debounced insurance search - only fetch when there's a search term
  useEffect(() => {
    // Only fetch if there's a search term
    if (!insuranceSearchTerm.trim()) {
      return
    }

    const delayDebounceFn = setTimeout(() => {
      fetchInsuranceCompanies(insuranceSearchTerm)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [insuranceSearchTerm])

  // Effect for debounced specialty search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSpecialties(specialtySearchTerm)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [specialtySearchTerm])

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: compact ? 2 : 3,
        overflow: 'hidden',
        boxShadow: compact
          ? '0 2px 8px rgba(0,0,0,0.1)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        height: compact ? '60px' : 'auto',
        '&:hover': {
          boxShadow: compact
            ? '0 4px 12px rgba(0,0,0,0.15)'
            : '0 12px 40px rgba(0,0,0,0.15)',
          transform: compact ? 'none' : 'translateY(-2px)',
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: compact ? 'nowrap' : 'wrap',
          height: compact ? '100%' : 'auto',
          position: 'relative',
        }}
      >
        {/* Location Input */}
        <Box
          onMouseEnter={
            disableHoverExpand ? undefined : () => setHoveredField('location')
          }
          onMouseLeave={
            disableHoverExpand ? undefined : () => setHoveredField(null)
          }
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand
                ? 1
                : hoveredField === 'location'
                  ? 2
                  : hoveredField
                    ? 0.8
                    : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand ? 1 : hoveredField === 'location' ? 0 : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: disableHoverExpand
                ? '0%'
                : hoveredField === 'location'
                  ? 'auto'
                  : '0%',
            },
            minWidth: {
              md: disableHoverExpand
                ? '0'
                : hoveredField === 'location'
                  ? '100px'
                  : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition: disableHoverExpand
              ? 'none'
              : 'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            zIndex: disableHoverExpand
              ? 0
              : hoveredField === 'location'
                ? 1
                : 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: compact ? '100%' : 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                display: 'block',
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
                lineHeight: compact ? 1 : 'normal',
              }}
            >
              Search Location
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                freeSolo
                options={locationOptions}
                getOptionLabel={option => {
                  if (typeof option === 'string') return option
                  return option.description
                }}
                value={locationInput}
                inputValue={locationInput}
                // 🌟 FIXED: detect real Google place selection
                onChange={(event, value) => {
                  if (!value) {
                    setSelectedLocation(null)
                    // ✅ Update patient store
                    setStoreLocation({ address: '', lat: null, lng: null })
                    // Clear radius when location is cleared
                    setStoreRadius('')
                    onRadiusChange?.(null)
                    onLocationChange(null)
                    setLocationInput('')
                    return
                  }

                  if (typeof value === 'string') {
                    // manual input
                    setSelectedLocation({
                      address: value,
                      latitude: 0,
                      longitude: 0,
                    })
                    // ✅ Update patient store
                    setStoreLocation({
                      address: value,
                      lat: 0,
                      lng: 0,
                    })
                    onLocationChange(value)
                    setLocationInput(value)
                    return
                  }

                  // 🌟 User clicked a GOOGLE prediction
                  if (value.place_id) {
                    handleLocationSelect(value.place_id, value.description)
                  }
                }}
                onInputChange={(event, value) => {
                  setLocationInput(value)
                  onLocationChange(value)
                }}
                loading={isGoogleLoading || loadingLocations}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="City, State or Zip code"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <LocationOn
                              sx={{
                                color: accentColor,
                                fontSize: compact
                                  ? '1rem'
                                  : { sm: '1.2rem', md: '1.5rem' },
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {locationLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      disableUnderline: true,
                      sx: {
                        fontSize: compact
                          ? '0.75rem'
                          : { sm: '0.8rem', md: '0.875rem' },
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        py: compact ? 0.5 : 0,
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                  />
                )}
                filterOptions={x => x}
                noOptionsText={
                  locationInput
                    ? 'No locations found'
                    : 'Start typing to search locations'
                }
                sx={{ flex: 1 }}
              />
              <Tooltip title="Use current location">
                <IconButton
                  onClick={handleCurrentLocation}
                  disabled={gettingCurrentLocation}
                  sx={{
                    color: gettingCurrentLocation ? 'grey.400' : '#3BAE78',
                    p: compact ? 0 : 0,
                  }}
                >
                  {gettingCurrentLocation ? (
                    <CircularProgress size={10} />
                  ) : null}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Search Radius Input */}
        {onRadiusChange && (
          <Box
            onMouseEnter={
              disableHoverExpand ? undefined : () => setHoveredField('radius')
            }
            onMouseLeave={
              disableHoverExpand ? undefined : () => setHoveredField(null)
            }
            sx={{
              flexGrow: {
                xs: 1,
                sm: 1,
                md: disableHoverExpand
                  ? 1
                  : hoveredField === 'radius'
                    ? 2
                    : hoveredField
                      ? 0.8
                      : 1,
              },
              flexShrink: {
                xs: 1,
                sm: 1,
                md: disableHoverExpand ? 1 : hoveredField === 'radius' ? 0 : 1,
              },
              flexBasis: {
                xs: '100%',
                sm: '33%',
                md: disableHoverExpand
                  ? '0%'
                  : hoveredField === 'radius'
                    ? 'auto'
                    : '0%',
              },
              minWidth: {
                md: disableHoverExpand
                  ? '0'
                  : hoveredField === 'radius'
                    ? '100px'
                    : '0',
              },
              borderRight: showSearchButton
                ? { xs: 'none', md: '1px solid #e0e0e0' }
                : 'none',
              borderColor: 'grey.200',
              bgcolor: 'grey.50',
              transition: disableHoverExpand
                ? 'none'
                : 'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              zIndex: disableHoverExpand
                ? 0
                : hoveredField === 'radius'
                  ? 1
                  : 0,
              position: 'relative',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            <Box
              sx={{
                p: compact ? 1 : { sm: 1.5, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: compact ? '100%' : 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.500',
                  mb: compact ? 0.25 : 0.5,
                  display: 'block',
                  fontSize: compact
                    ? '0.6rem'
                    : { sm: '0.7rem', md: '0.75rem' },
                  lineHeight: compact ? 1 : 'normal',
                }}
              >
                Search Radius
              </Typography>
              <Autocomplete
                options={SEARCH_RADIUS_OPTIONS}
                value={radius}
                onChange={(e, v) => {
                  if (!location || !location.trim()) return
                  onRadiusChange(v)
                  // ✅ Update patient store
                  setStoreRadius(v?.replace(' miles', '') || '')
                }}
                disabled={!location || !location.trim()}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={
                      !location || !location.trim()
                        ? 'Select location first'
                        : 'Select radius'
                    }
                    variant="standard"
                    disabled={!location || !location.trim()}
                    helperText={
                      !location || !location.trim()
                        ? 'Location required'
                        : undefined
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <MyLocation
                            sx={{
                              color:
                                !location || !location.trim()
                                  ? 'grey.400'
                                  : accentColor,
                              fontSize: compact
                                ? '1rem'
                                : { sm: '1.2rem', md: '1.5rem' },
                            }}
                          />
                        </InputAdornment>
                      ),
                      disableUnderline: true,
                      sx: {
                        fontSize: compact
                          ? '0.75rem'
                          : { sm: '0.8rem', md: '0.875rem' },
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        py: compact ? 0.5 : 0,
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {/* Insurance Input */}
        <Box
          onMouseEnter={
            disableHoverExpand ? undefined : () => setHoveredField('insurance')
          }
          onMouseLeave={
            disableHoverExpand ? undefined : () => setHoveredField(null)
          }
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand
                ? 1
                : hoveredField === 'insurance'
                  ? 2
                  : hoveredField
                    ? 0.8
                    : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand ? 1 : hoveredField === 'insurance' ? 0 : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: disableHoverExpand
                ? '0%'
                : hoveredField === 'insurance'
                  ? 'auto'
                  : '0%',
            },
            minWidth: {
              md: disableHoverExpand
                ? '0'
                : hoveredField === 'insurance'
                  ? '100px'
                  : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition: disableHoverExpand
              ? 'none'
              : 'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            zIndex: disableHoverExpand
              ? 0
              : hoveredField === 'insurance'
                ? 1
                : 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: compact ? '100%' : 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                display: 'block',
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
                lineHeight: compact ? 1 : 'normal',
              }}
            >
              Insurance
            </Typography>
            <Autocomplete
              freeSolo
              loading={loadingCompanies}
              options={insuranceCompanies}
              getOptionLabel={option => {
                if (typeof option === 'string') return option
                return option.payer_name
              }}
              value={insurance}
              onChange={(e, v) => {
                const name = typeof v === 'string' ? v : v?.payer_name || null
                const id = typeof v === 'string' ? null : v?._id || null
                onInsuranceChange(name)
                // ✅ Update patient store
                setInsurance(id, name)
              }}
              onInputChange={(e, v, reason) => {
                setInsuranceSearchTerm(v)
                onInsuranceChange(v)
                // ✅ Update patient store for input changes
                if (reason === 'input') {
                  setInsurance(null, v || null)
                } else if (reason === 'clear') {
                  setInsurance(null, null)
                }
              }}
              filterOptions={(options, state) => {
                if (!state.inputValue) return options
                return options.filter(option =>
                  option.payer_name
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase())
                )
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={typeof option === 'string' ? option : option._id}
                >
                  {typeof option === 'string' ? option : option.payer_name}
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select Insurance"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <HealthAndSafety
                          sx={{
                            color: accentColor,
                            fontSize: compact
                              ? '1rem'
                              : { sm: '1.2rem', md: '1.5rem' },
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingCompanies ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    disableUnderline: true,
                    sx: {
                      fontSize: compact
                        ? '0.75rem'
                        : { sm: '0.8rem', md: '0.875rem' },
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      py: compact ? 0.5 : 0,
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Specialization Input - FULLY FIXED */}
        <Box
          onMouseEnter={
            disableHoverExpand
              ? undefined
              : () => setHoveredField('specialization')
          }
          onMouseLeave={
            disableHoverExpand ? undefined : () => setHoveredField(null)
          }
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand
                ? 1
                : hoveredField === 'specialization'
                  ? 2
                  : hoveredField
                    ? 0.8
                    : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: disableHoverExpand
                ? 1
                : hoveredField === 'specialization'
                  ? 0
                  : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: disableHoverExpand
                ? '0%'
                : hoveredField === 'specialization'
                  ? 'auto'
                  : '0%',
            },
            minWidth: {
              md: disableHoverExpand
                ? '0'
                : hoveredField === 'specialization'
                  ? '100px'
                  : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition: disableHoverExpand
              ? 'none'
              : 'flex-grow .4s, min-width .4s, flex-shrink .4s',
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
              }}
            >
              Specialization
            </Typography>

            <Autocomplete
              freeSolo
              loading={loadingSpecialties}
              options={specialties}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option.name || ''
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              // SELECTED VALUE
              value={
                specialtyName ? { _id: specialtyId, name: specialtyName } : null
              }
              // INPUT TEXT DISPLAYED
              inputValue={specialtySearchTerm}
              // USER TYPING
              onInputChange={(e, v, reason) => {
                if (reason === 'input') {
                  setSpecialtySearchTerm(v)
                  onNeedsChange(v)
                }
                if (reason === 'clear') {
                  setSpecialtySearchTerm('')
                  setSpecialty(null, null)
                  onNeedsChange(null)
                }
              }}
              // USER SELECTS FROM DROPDOWN OR FREE-TYPES
              onChange={(event, value) => {
                if (!value) {
                  // CLEAR
                  setSpecialty(null, null)
                  setSpecialtySearchTerm('')
                  onNeedsChange(null)
                  return
                }

                if (typeof value === 'string') {
                  // FREE TEXT
                  setSpecialty(null, value)
                  setSpecialtySearchTerm(value) // <-- FIX: SHOW IN INPUT
                  onNeedsChange(value)
                  return
                }

                // DROPDOWN SELECT
                setSpecialty(value._id, value.name)
                setSpecialtySearchTerm(value.name) // <-- FIX: SHOW IN INPUT
                onNeedsChange(value.name)
              }}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  {option.name}
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Enter specialization"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Psychology sx={{ color: accentColor }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingSpecialties && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    disableUnderline: true,
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Search Button */}
        {showSearchButton && onSearch && (
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isSearchLoading}
            sx={{
              bgcolor: accentColor,
              borderRadius: 0,
              minWidth: compact
                ? '50px'
                : { xs: '100%', md: '60px', lg: '80px' },
              flex: { xs: '1 1 100%', md: '0 0 auto' },
              height: compact ? '100%' : 'auto',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: accentHoverColor,
                transform: compact ? 'none' : 'scale(1.05)',
                boxShadow: `0 4px 12px ${accentColor}4D`,
              },
            }}
          >
            {isSearchLoading ? (
              <CircularProgress
                size={compact ? 20 : 28}
                sx={{ color: 'white' }}
              />
            ) : (
              <Search
                sx={{
                  color: 'white',
                  fontSize: compact ? '1.2rem' : { sm: '1.5rem', md: '2rem' },
                }}
              />
            )}
          </Button>
        )}
      </Box>
    </Paper>
  )
}

export default SearchBarForPatientLanding

// import React from 'react'
// import {
//   Box,
//   Button,
//   Paper,
//   Typography,
//   Autocomplete,
//   TextField,
//   InputAdornment,
//   SxProps,
//   Theme,
//   SvgIconProps,
//   CircularProgress,
// } from '@mui/material'
// import {
//   LocationOn,
//   Search,
//   HealthAndSafety,
//   Psychology,
//   MyLocation,
// } from '@mui/icons-material'

// export const SEARCH_RADIUS_OPTIONS = [
//   '5 miles',
//   '10 miles',
//   // '15 miles',
//   '20 miles',
//   '40 miles',
// ]

// type AutocompleteOption = Record<string, any> | string

// interface SearchBarInPatientLandingProps {
//   location: string | null
//   insurance: string | null
//   specialization: string | null
//   radius?: string | null
//   locationOptions?: AutocompleteOption[]
//   insuranceOptions?: AutocompleteOption[]
//   specializationOptions?: AutocompleteOption[]
//   locationLoading?: boolean
//   insuranceLoading?: boolean
//   specializationLoading?: boolean
//   onLocationChange: (value: string | null) => void
//   onInsuranceChange: (value: string | null) => void
//   onSpecializationChange: (value: string | null) => void
//   onRadiusChange?: (value: string | null) => void
//   onLocationSelect?: (option: AutocompleteOption | null) => void
//   onInsuranceSelect?: (option: AutocompleteOption | null) => void
//   onSpecializationSelect?: (option: AutocompleteOption | null) => void
//   onLocationInputChange?: (value: string) => void
//   onInsuranceInputChange?: (value: string) => void
//   onSpecializationInputChange?: (value: string) => void
//   onSearch?: () => void
//   isSearchLoading?: boolean
//   showSearchButton?: boolean
//   compact?: boolean
//   sx?: SxProps<Theme>
//   accentColor?: string
//   accentHoverColor?: string
//   thirdFieldLabel?: string
//   thirdFieldPlaceholder?: string
//   thirdFieldIcon?: React.ElementType<SvgIconProps>
// }

// const getOptionLabel = (option: AutocompleteOption, fallback = '') => {
//   if (!option) return fallback
//   if (typeof option === 'string') return option
//   return (
//     option.label ??
//     option.name ??
//     option.payer_name ??
//     option.description ??
//     option.title ??
//     fallback
//   )
// }

// const SearchBarInPatientLanding: React.FC<SearchBarInPatientLandingProps> = ({
//   location,
//   insurance,
//   specialization,
//   radius,
//   locationOptions,
//   insuranceOptions,
//   specializationOptions,
//   locationLoading = false,
//   insuranceLoading = false,
//   specializationLoading = false,
//   onLocationChange,
//   onInsuranceChange,
//   onSpecializationChange,
//   onRadiusChange,
//   onLocationSelect,
//   onInsuranceSelect,
//   onSpecializationSelect,
//   onLocationInputChange,
//   onInsuranceInputChange,
//   onSpecializationInputChange,
//   onSearch,
//   isSearchLoading = false,
//   showSearchButton = true,
//   compact = false,
//   sx,
//   accentColor = '#2E7DAF',
//   accentHoverColor = '#2563a0',
//   thirdFieldLabel = 'Specialization',
//   thirdFieldPlaceholder = 'Enter specialization',
//   thirdFieldIcon,
// }) => {
//   const [hoveredField, setHoveredField] = React.useState<
//     'location' | 'radius' | 'insurance' | 'specialization' | null
//   >(null)

//   const ThirdFieldIcon = thirdFieldIcon ?? Psychology
//   const locationOptionsToUse: AutocompleteOption[] = locationOptions ?? []
//   const insuranceOptionsToUse: AutocompleteOption[] = insuranceOptions ?? []
//   const specializationOptionsToUse: AutocompleteOption[] =
//     specializationOptions ?? []

//   return (
//     <Paper
//       elevation={3}
//       sx={{
//         borderRadius: compact ? 2 : 3,
//         overflow: 'hidden',
//         boxShadow: compact
//           ? '0 2px 8px rgba(0,0,0,0.1)'
//           : '0 8px 32px rgba(0,0,0,0.1)',
//         transition: 'all 0.3s ease',
//         height: compact ? '60px' : 'auto',
//         '&:hover': {
//           boxShadow: compact
//             ? '0 4px 12px rgba(0,0,0,0.15)'
//             : '0 12px 40px rgba(0,0,0,0.15)',
//           transform: compact ? 'none' : 'translateY(-2px)',
//         },
//         ...sx,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           alignItems: 'stretch',
//           flexWrap: compact ? 'nowrap' : 'wrap',
//           height: compact ? '100%' : 'auto',
//           position: 'relative',
//         }}
//       >
//         {/* Location Input */}
//         <Box
//           onMouseEnter={() => setHoveredField('location')}
//           onMouseLeave={() => setHoveredField(null)}
//           sx={{
//             flexGrow: {
//               xs: 1,
//               sm: 1,
//               md: hoveredField === 'location' ? 2 : hoveredField ? 0.8 : 1,
//             },
//             flexShrink: {
//               xs: 1,
//               sm: 1,
//               md: hoveredField === 'location' ? 0 : 1,
//             },
//             flexBasis: {
//               xs: '100%',
//               sm: '33%',
//               md: hoveredField === 'location' ? 'auto' : '0%',
//             },
//             minWidth: {
//               md: hoveredField === 'location' ? '100px' : '0',
//             },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition:
//               'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             zIndex: hoveredField === 'location' ? 1 : 0,
//             position: 'relative',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Add location
//             </Typography>
//             <Autocomplete
//               freeSolo
//               options={locationOptionsToUse}
//               loading={locationLoading}
//               getOptionLabel={option =>
//                 getOptionLabel(option as AutocompleteOption)
//               }
//               isOptionEqualToValue={(option, value) =>
//                 getOptionLabel(option as AutocompleteOption) ===
//                 (typeof value === 'string'
//                   ? value
//                   : getOptionLabel(value as AutocompleteOption))
//               }
//               value={location || ''}
//               onChange={(event, newValue) => {
//                 const nextValue =
//                   typeof newValue === 'string'
//                     ? newValue
//                     : getOptionLabel(newValue as AutocompleteOption, '')
//                 onLocationChange(nextValue)
//                 onLocationSelect?.(
//                   typeof newValue === 'string'
//                     ? { label: newValue }
//                     : (newValue as AutocompleteOption)
//                 )
//               }}
//               onInputChange={(event, newInputValue, reason) => {
//                 if (reason === 'input' || reason === 'clear') {
//                   onLocationChange(newInputValue)
//                   onLocationInputChange?.(newInputValue)
//                 }
//               }}
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder="City, State or Zip code"
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <LocationOn
//                             sx={{
//                               color: accentColor,
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     endAdornment: (
//                       <>
//                         {locationLoading ? (
//                           <CircularProgress color="inherit" size={16} />
//                         ) : null}
//                         {params.InputProps.endAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Search Radius Input */}
//         {onRadiusChange && (
//           <Box
//             onMouseEnter={() => setHoveredField('radius')}
//             onMouseLeave={() => setHoveredField(null)}
//             sx={{
//               flexGrow: {
//                 xs: 1,
//                 sm: 1,
//                 md: hoveredField === 'radius' ? 2 : hoveredField ? 0.8 : 1,
//               },
//               flexShrink: {
//                 xs: 1,
//                 sm: 1,
//                 md: hoveredField === 'radius' ? 0 : 1,
//               },
//               flexBasis: {
//                 xs: '100%',
//                 sm: '33%',
//                 md: hoveredField === 'radius' ? 'auto' : '0%',
//               },
//               minWidth: {
//                 md: hoveredField === 'radius' ? '100px' : '0',
//               },
//               borderRight: showSearchButton
//                 ? { xs: 'none', md: '1px solid #e0e0e0' }
//                 : 'none',
//               borderColor: 'grey.200',
//               bgcolor: 'grey.50',
//               transition:
//                 'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               zIndex: hoveredField === 'radius' ? 1 : 0,
//               position: 'relative',
//               '&:hover': {
//                 bgcolor: 'grey.100',
//               },
//             }}
//           >
//             <Box
//               sx={{
//                 p: compact ? 1 : { sm: 1.5, md: 2 },
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 height: compact ? '100%' : 'auto',
//               }}
//             >
//               <Typography
//                 variant="caption"
//                 sx={{
//                   color: 'grey.500',
//                   mb: compact ? 0.25 : 0.5,
//                   display: 'block',
//                   fontSize: compact
//                     ? '0.6rem'
//                     : { sm: '0.7rem', md: '0.75rem' },
//                   lineHeight: compact ? 1 : 'normal',
//                 }}
//               >
//                 Search Radius
//               </Typography>
//               <Autocomplete
//                 options={SEARCH_RADIUS_OPTIONS}
//                 value={radius}
//                 onChange={(event, newValue) => onRadiusChange(newValue)}
//                 renderInput={params => (
//                   <TextField
//                     {...params}
//                     placeholder="Select radius"
//                     variant="standard"
//                     InputProps={{
//                       ...params.InputProps,
//                       startAdornment: (
//                         <>
//                           <InputAdornment position="start">
//                             <MyLocation
//                               sx={{
//                                 color: accentColor,
//                                 fontSize: compact
//                                   ? '1rem'
//                                   : { sm: '1.2rem', md: '1.5rem' },
//                                 transition: 'transform 0.2s ease',
//                                 '&:hover': {
//                                   transform: 'scale(1.1)',
//                                 },
//                               }}
//                             />
//                           </InputAdornment>
//                           {params.InputProps.startAdornment}
//                         </>
//                       ),
//                       disableUnderline: true,
//                       sx: {
//                         fontSize: compact
//                           ? '0.75rem'
//                           : { sm: '0.8rem', md: '0.875rem' },
//                         height: compact ? '24px' : 'auto',
//                       },
//                     }}
//                     sx={{
//                       '& .MuiInputBase-input': {
//                         py: compact ? 0.5 : 0,
//                         height: compact ? '24px' : 'auto',
//                       },
//                     }}
//                   />
//                 )}
//               />
//             </Box>
//           </Box>
//         )}

//         {/* Insurance Input */}
//         <Box
//           onMouseEnter={() => setHoveredField('insurance')}
//           onMouseLeave={() => setHoveredField(null)}
//           sx={{
//             flexGrow: {
//               xs: 1,
//               sm: 1,
//               md: hoveredField === 'insurance' ? 2 : hoveredField ? 0.8 : 1,
//             },
//             flexShrink: {
//               xs: 1,
//               sm: 1,
//               md: hoveredField === 'insurance' ? 0 : 1,
//             },
//             flexBasis: {
//               xs: '100%',
//               sm: '33%',
//               md: hoveredField === 'insurance' ? 'auto' : '0%',
//             },
//             minWidth: {
//               md: hoveredField === 'insurance' ? '100px' : '0',
//             },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition:
//               'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             zIndex: hoveredField === 'insurance' ? 1 : 0,
//             position: 'relative',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Insurance
//             </Typography>
//             <Autocomplete
//               freeSolo
//               options={insuranceOptionsToUse}
//               loading={insuranceLoading}
//               getOptionLabel={option =>
//                 getOptionLabel(option as AutocompleteOption)
//               }
//               filterOptions={(options, state) => options}
//               isOptionEqualToValue={(option, value) =>
//                 getOptionLabel(option as AutocompleteOption) ===
//                 (typeof value === 'string'
//                   ? value
//                   : getOptionLabel(value as AutocompleteOption))
//               }
//               value={insurance || ''}
//               onChange={(event, newValue) => {
//                 const nextValue =
//                   typeof newValue === 'string'
//                     ? newValue
//                     : getOptionLabel(newValue as AutocompleteOption, '')
//                 onInsuranceChange(nextValue)
//                 onInsuranceSelect?.(
//                   typeof newValue === 'string'
//                     ? { label: newValue }
//                     : (newValue as AutocompleteOption)
//                 )
//               }}
//               onInputChange={(event, newInputValue, reason) => {
//                 if (reason === 'input' || reason === 'clear') {
//                   onInsuranceChange(newInputValue)
//                   onInsuranceInputChange?.(newInputValue)
//                 }
//               }}
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder="Select Insurance"
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <HealthAndSafety
//                             sx={{
//                               color: accentColor,
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     endAdornment: (
//                       <>
//                         {insuranceLoading ? (
//                           <CircularProgress color="inherit" size={16} />
//                         ) : null}
//                         {params.InputProps.endAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Specialization Input */}
//         <Box
//           onMouseEnter={() => setHoveredField('specialization')}
//           onMouseLeave={() => setHoveredField(null)}
//           sx={{
//             flexGrow: {
//               xs: 1,
//               sm: 1,
//               md:
//                 hoveredField === 'specialization' ? 2 : hoveredField ? 0.8 : 1,
//             },
//             flexShrink: {
//               xs: 1,
//               sm: 1,
//               md: hoveredField === 'specialization' ? 0 : 1,
//             },
//             flexBasis: {
//               xs: '100%',
//               sm: '33%',
//               md: hoveredField === 'specialization' ? 'auto' : '0%',
//             },
//             minWidth: {
//               md: hoveredField === 'specialization' ? '100px' : '0',
//             },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition:
//               'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             zIndex: hoveredField === 'specialization' ? 1 : 0,
//             position: 'relative',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               {thirdFieldLabel}
//             </Typography>
//             <Autocomplete
//               freeSolo
//               options={specializationOptionsToUse}
//               loading={specializationLoading}
//               getOptionLabel={option =>
//                 getOptionLabel(option as AutocompleteOption)
//               }
//               filterOptions={(options, state) => options}
//               isOptionEqualToValue={(option, value) =>
//                 getOptionLabel(option as AutocompleteOption) ===
//                 (typeof value === 'string'
//                   ? value
//                   : getOptionLabel(value as AutocompleteOption))
//               }
//               value={specialization || ''}
//               onChange={(event, newValue) => {
//                 const nextValue =
//                   typeof newValue === 'string'
//                     ? newValue
//                     : getOptionLabel(newValue as AutocompleteOption, '')
//                 onSpecializationChange(nextValue)
//                 onSpecializationSelect?.(
//                   typeof newValue === 'string'
//                     ? { label: newValue }
//                     : (newValue as AutocompleteOption)
//                 )
//               }}
//               onInputChange={(event, newInputValue, reason) => {
//                 if (reason === 'input' || reason === 'clear') {
//                   onSpecializationChange(newInputValue)
//                   onSpecializationInputChange?.(newInputValue)
//                 }
//               }}
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder={thirdFieldPlaceholder}
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <ThirdFieldIcon
//                             sx={{
//                               color: accentColor,
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     endAdornment: (
//                       <>
//                         {specializationLoading ? (
//                           <CircularProgress color="inherit" size={16} />
//                         ) : null}
//                         {params.InputProps.endAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Search Button */}
//         {showSearchButton && onSearch && (
//           <Button
//             variant="contained"
//             onClick={onSearch}
//             disabled={isSearchLoading}
//             sx={{
//               bgcolor: accentColor,
//               borderRadius: 0,
//               minWidth: compact
//                 ? '50px'
//                 : { xs: '100%', md: '60px', lg: '80px' },
//               flex: { xs: '1 1 100%', md: '0 0 auto' },
//               height: compact ? '100%' : 'auto',
//               transition: 'all 0.3s ease',
//               '&:hover': {
//                 bgcolor: accentHoverColor,
//                 transform: compact ? 'none' : 'scale(1.05)',
//                 boxShadow: `0 4px 12px ${accentColor}4D`,
//               },
//             }}
//           >
//             {isSearchLoading ? (
//               <CircularProgress
//                 size={compact ? 20 : 28}
//                 sx={{ color: 'white' }}
//               />
//             ) : (
//               <Search
//                 sx={{
//                   color: 'white',
//                   fontSize: compact ? '1.2rem' : { sm: '1.5rem', md: '2rem' },
//                 }}
//               />
//             )}
//           </Button>
//         )}
//       </Box>
//     </Paper>
//   )
// }

// export default SearchBarInPatientLanding
